import { forwardRef } from 'react';
import { ListItem as MuiListItem, ListItemProps as MuiListItemProps } from '@mui/material';

/**
 * ListItem component - Base container for list item content.
 *
 * This is a thin wrapper around Material-UI's ListItem component that provides:
 * - Flexible content container for list items
 * - Divider support for visual separation
 * - Gutter control (disableGutters)
 * - Selected state styling
 * - Theme integration
 * - Full accessibility (semantic <li> element)
 *
 * Common use cases:
 * - Static list items with text and icons
 * - Container for complex list item layouts
 * - Items in navigation and settings lists
 *
 * @example
 * // Basic list item
 * <ListItem>
 *   <ListItemText primary="Item text" />
 * </ListItem>
 *
 * @example
 * // With icon
 * <ListItem>
 *   <ListItemIcon><CheckIcon /></ListItemIcon>
 *   <ListItemText primary="Completed" />
 * </ListItem>
 *
 * @example
 * // With divider
 * <ListItem divider>
 *   <ListItemText primary="Item with divider" />
 * </ListItem>
 */
export type ListItemProps = MuiListItemProps;

export const ListItem = forwardRef<HTMLLIElement, ListItemProps>((props, ref) => (
  <MuiListItem ref={ref} {...props} />
));

ListItem.displayName = 'ListItem';
